import { useState } from "react";

import { useSaveResourceAction } from "~/ai-chat/feature/use-save-resource-action";
import type { SaveResourceAction as SaveResourceActionModel } from "~/ai-chat/model/save-resource-action";
import { ChatSaveResourceCard, type SaveResourceCardState } from "~/ai-chat/ui/chat-save-resource-card";
import { useGetPlatformInfosecSettings } from "~/api/useInfosecLevels";
import { useAllowedInfosecLevels, useInfosecFeatureEnabled } from "~/hooks";
import { buildResourcePath } from "~/routes";

interface ChatSaveResourceActionProps {
  action: SaveResourceActionModel;
}

export function ChatSaveResourceAction({ action }: ChatSaveResourceActionProps) {
  const [cardState, setCardState] = useState<SaveResourceCardState>({ status: "ready" });
  const { save } = useSaveResourceAction();
  const featureEnabled = useInfosecFeatureEnabled();
  const platformSettingsQuery = useGetPlatformInfosecSettings();
  const infosecLevels = useAllowedInfosecLevels(undefined, { enabled: featureEnabled });

  // Off feature shell ⇒ definitively false so Save is never blocked; on an infosec
  // deployment `undefined` keeps Save blocked until the platform setting resolves.
  const infosecEnabled = featureEnabled ? platformSettingsQuery.data?.enabled : false;

  const handleSave = async (edited: SaveResourceActionModel, infosecLevelId?: string) => {
    setCardState({ status: "saving" });
    try {
      const saved = await save(edited, infosecLevelId);
      setCardState({
        status: "saved",
        link: { type: "model", path: buildResourcePath({ resourceId: saved.resourceId }) },
        warning: saved.infosecApplied
          ? undefined
          : "The infosec level could not be applied. Set it from the resource page.",
      });
    } catch {
      setCardState({ status: "error", message: "Could not save the resource. Please try again." });
    }
  };

  return (
    <ChatSaveResourceCard
      action={action}
      state={cardState}
      infosecEnabled={infosecEnabled}
      infosecLevels={infosecLevels}
      onSave={(edited, infosecLevelId) => void handleSave(edited, infosecLevelId)}
    />
  );
}
